// COP Copilot — the capability skeleton (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// Folds takbot ("COP Copilot") into the COP as a C3 capability. It mirrors chatCapability.ts
// (publish + own-a-slideout) and tracksCapability's factory shape: createCopilotCapability(opts)
// returns { capability, api } so the host registers the Capability and App.tsx mounts
// <CopilotPanel api={api} />.
//
// THE SECURITY FOLD-IN (the load-bearing change vs takbot's broker):
//   1. READ from the HOST-FILTERED bus ONLY. activate() subscribes through ctx.data.subscribe,
//      which the COP host has PDP-interposed (cop.ts:726-740 — pdp.isVisibleCached). Every
//      delivered envelope is already cleared-visible for the logged-in persona; a denied
//      envelope is never delivered (no marker, no count — Glomar). The copilot's working set
//      (copilotTables.ts) is fed EXCLUSIVELY from this seam — there is NO static `visible:true`
//      placeholder and no private TableRegistry that could widen what the persona sees.
//   2. NEVER write the engine. takbot's broker drove a MapController directly (deps.map.*).
//      Here every maps.* verb is REROUTED to a host-mediated action:
//        - highlight_rows / clear_highlights / filter_layer → CommandSpecs that record intent
//          (Wave A) / are applied by the host's sole-writer paint path (Wave B). The capability
//          never touches engine.highlightRows / clearHighlights / filterLayer.
//        - set_viewport → ctx.engine.setView(...) — the ONE map verb that survives directly
//          (setView is on the write-free CapabilityEngine camera surface, not Omitted).
//        - request_evac → the scop:cmd:request-evac CommandSpec (copilotEvac.ts), which publishes
//          an alert(subtype:'evac') envelope the host paints — NO direct deps.map.placeEvac.
//      So apps/cop/scripts/no-bypass-check.ts passes: the only engine member this file touches is
//      the allowed read-surface setView.
//
// Holds NO React (that lives in CopilotPanel.tsx). Imports only TYPES from the C3 SDK + the
// sibling copilot modules.

import type {
  Capability,
  HostContext,
  Disposable,
  CanonicalEnvelope,
  CommandSpec,
} from "../../../../packages/contracts-ts/src/index.ts";
import { CopilotWorkingSet } from "./copilotTables.ts";
import { executeQuery, type TableQuery, QueryError } from "./copilotGeoQuery.ts";
import { createCopilotApi, type CopilotApi, type CopilotApiOptions, type CopilotRunResult, type CopilotListItem } from "./copilotApi.ts";
import { createRequestEvacCommand, placeholderSelfLocation, type SelfLocation } from "./copilotEvac.ts";
import { selectBackend, type Backend } from "./copilot/runtime/backends/index.ts";
import { runAgentTurn } from "./copilot/runtime/driver.ts";
import { buildManifest, makeExecuteToolCall, ResultRefStore } from "./copilot/runtimeBridge.ts";
import {
  COPILOT_CAPABILITY_ID,
  COPILOT_CMD_ASK,
  COPILOT_CMD_SUBMIT,
  COPILOT_CMD_QUERY,
  COPILOT_CMD_HIGHLIGHT,
  COPILOT_CMD_CLEAR_HIGHLIGHTS,
  COPILOT_CMD_FILTER_LAYER,
} from "./copilotManifest.ts";

/** The envelope kinds the copilot reads from the host-filtered bus into its working set.
 *  (Entities + zones; everything the Q1 read surface targets.) */
const COPILOT_KINDS: CanonicalEnvelope["kind"][] = [
  "track",
  "observation",
  "alert",
  "geometry",
  "asset",
  "task",
  "sensor",
  "cyber-object",
];

/** Construction options for the COP copilot capability. */
export interface CopilotCapabilityOptions {
  /** Stable capability id (default "scop:cap:copilot"). */
  id?: string;
  /** Display label for the analyst session (default "COP Copilot"). */
  persona?: string;
  /** Short acting-subject tag for the panel header ("ACTING AS {personaShort}"). Display only —
   *  NOT a security boundary; the host-filtered bus is the only thing that gates the working set. */
  personaShort?: string;
  /** Host-owned self-location accessor (placeholder in Wave A; HostContext.selfLocation is
   *  upstream-unauthored). The capability/agent NEVER supplies coordinates. */
  selfLocation?: () => SelfLocation;
  /** Optional seed turns so the panel looks alive on first paint. */
  seed?: CopilotApiOptions["seed"];
}

/** A copilot capability + its panel-facing API (mirrors ChatCapabilityHandle). */
export interface CopilotCapabilityHandle {
  capability: Capability;
  api: CopilotApi;
}

/** Coerce free-form command args into a string of intent text. */
function argText(args: unknown): string {
  if (typeof args === "string") return args;
  if (args && typeof args === "object" && "text" in args) return String((args as { text: unknown }).text);
  return "";
}

/** Read a string field off a preview row (the cleared-rows projection from runtimeBridge). */
function rowStr(row: Record<string, unknown>, key: string): string | undefined {
  const v = row[key];
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : undefined;
}

/** Normalize a free-form affiliation string to the panel's APP-6 dot bucket (Rule 2: hue =
 *  affiliation only). Anything we can't map falls back to the unknown hue. */
function affBucket(raw: string | undefined): CopilotListItem["affiliation"] {
  const a = (raw ?? "").toLowerCase();
  if (a.includes("host") || a.includes("enemy") || a.includes("threat")) return "hostile";
  if (a.includes("friend") || a.includes("blue") || a.includes("ally")) return "friendly";
  if (a.includes("neut") || a.includes("green")) return "neutral";
  if (a.includes("pend") || a.includes("suspect") || a.includes("assumed")) return "pending";
  return "unknown";
}

/** C2 — THE STRUCTURED-OUTPUT POST-PROCESSOR. Map the cleared preview rows the runtime ACTUALLY
 *  cited (the last table.query's PDP-confined working-set rows) into the panel's ranked list[].
 *  We NEVER fabricate ranked data: every row here came back from executeQuery over the visible
 *  working set (Glomar by construction — a denied object is absent upstream). `ref` is the row id
 *  (the CITE-chip target → handle.select via the panel→map seam). Caps at 8 so the bottom-center
 *  card stays compact; rank is the row's order in the (already-ordered) preview. */
function listFromPreviewRows(rows: Array<Record<string, unknown>>): CopilotListItem[] {
  const out: CopilotListItem[] = [];
  let rank = 0;
  for (const row of rows) {
    const ref = rowStr(row, "id");
    const call = rowStr(row, "name") ?? rowStr(row, "callsign") ?? ref;
    if (!call) continue; // a row with no displayable label is skipped, never invented
    rank += 1;
    // meta: the secondary line — type · classification (mono), assembled from whatever the row carries.
    const metaParts = [rowStr(row, "type") ?? rowStr(row, "domain"), rowStr(row, "classification")].filter(
      (p): p is string => typeof p === "string",
    );
    out.push({
      rank,
      call,
      meta: metaParts.join(" · "),
      affiliation: affBucket(rowStr(row, "affiliation")),
      ...(ref ? { ref } : {}),
    });
    if (out.length >= 8) break;
  }
  return out;
}

/** Create the COP copilot capability. Returns the Capability for the host AND the CopilotApi
 *  for the panel (mirrors createChatCapability's factory shape). */
export function createCopilotCapability(opts: CopilotCapabilityOptions = {}): CopilotCapabilityHandle {
  const id = opts.id ?? COPILOT_CAPABILITY_ID;
  const selfLocation = opts.selfLocation ?? placeholderSelfLocation;

  let ctx: HostContext | undefined;
  let disposables: Disposable[] = [];

  /** THE WORKING SET — fed ONLY from the host-filtered (PDP-visible) subscription. */
  const workingSet = new CopilotWorkingSet();

  /** THE LIFTED AGENT RUNTIME RUNNER. ask() drives this: the clearance-aware system prompt + the
   *  user query + the copilot's MCP tools executing the think⇄instant tool loop OVER THE CONFINED
   *  (PDP-visible) working set ONLY. The backend is the local Qwen3.6 (OpenAI /v1) when reachable,
   *  else the deterministic MockBackend (auto-fallback) — so the COP never hard-depends on a model.
   *  Backend selection (a live /models probe) is done lazily on first ask + cached. The model only
   *  ever sees tool-RESULT previews of already-cleared rows (runtimeBridge) — never a raw envelope,
   *  a sealed payload, or a denied-object count (Glomar). */
  let backendPromise: Promise<Backend> | undefined;
  let runSeq = 0;
  const runner = async (text: string): Promise<CopilotRunResult> => {
    if (!backendPromise) backendPromise = selectBackend();
    const backend = await backendPromise;
    const manifest = buildManifest(workingSet);
    // A per-turn result_ref store so a query→highlight chain references the same cleared rows.
    const execute = makeExecuteToolCall(workingSet, new ResultRefStore());
    runSeq += 1;
    const outcome = await runAgentTurn(backend, manifest, `copilot-${runSeq}`, text, execute);
    // Surface the LAST successful query preview as the panel's structured result (visible-only).
    const lastQuery = [...outcome.steps].reverse().find(
      (s) => s.result.ok && s.call.capability === "table.query",
    );
    let result: CopilotRunResult["result"];
    // C2: the structured ranked list, derived from the SAME cleared preview rows (never fabricated).
    let list: CopilotListItem[] | undefined;
    if (lastQuery && lastQuery.result.ok) {
      const r = lastQuery.result.result as { preview?: { columns?: string[]; rows?: Array<Record<string, unknown>> }; rows_returned?: number } | null;
      const preview = r?.preview;
      if (preview && Array.isArray(preview.rows)) {
        result = {
          columns: preview.columns ?? [],
          rows: preview.rows,
          rows_returned: typeof r?.rows_returned === "number" ? r.rows_returned : preview.rows.length,
        };
        const ranked = listFromPreviewRows(preview.rows);
        if (ranked.length > 0) list = ranked;
      }
    }
    return {
      text: outcome.final.text,
      ...(outcome.final.citations.length > 0 ? { citations: outcome.final.citations } : {}),
      ...(outcome.final.marking ? { marking: outcome.final.marking } : {}),
      ...(outcome.final.coverage_warning ? { coverage: outcome.final.coverage_warning } : {}),
      ...(result ? { result } : {}),
      ...(list ? { list } : {}),
    };
  };

  // FULL runtime-state wipe: drop the read-side working set AND force backend/run-seq re-init. Used
  // for BOTH the lifecycle purge (reset() → clearWorkingSet, on persona switch / logout) and the
  // downgrade security wipe (eject() → onEject), so NEITHER path leaves a higher-cls working-set row
  // OR a stale backend/run cursor bound to the prior identity/clearance behind. A persona switch is
  // an identity boundary just as a downgrade is a clearance boundary — both demand a fresh backend.
  // (CONTRACT:C3-CAPABILITY-PLUGIN.1.0)
  const wipeRuntimeState = (): void => {
    workingSet.clear();
    backendPromise = undefined;
    runSeq = 0;
  };

  /** The panel-facing API. ask() drives the lifted runtime runner over the visible working set;
   *  the canned working-set summary remains the no-runner fallback. Never reaches HostContext. */
  const api = createCopilotApi({
    persona: opts.persona ?? "COP Copilot",
    ...(opts.personaShort ? { personaShort: opts.personaShort } : {}),
    workingSet: { size: () => workingSet.size(), manifest: () => workingSet.manifest() },
    runner,
    // EPHEMERALITY seam: reset() (host-driven on persona switch / query-level downgrade / logout)
    // wipes the read-side working set AND the backend cursor through this hook, so no higher-cls
    // row OR prior-identity reasoning survives into a lower view. The host-filtered subscription
    // re-ingests the now-visible census after.
    clearWorkingSet: wipeRuntimeState,
    ...(opts.seed ? { seed: opts.seed } : {}),
    // SECURITY-state wipe on classification downgrade (cop.ts setQueryLevel → copilotApi.eject()).
    onEject: wipeRuntimeState,
  });

  /** Run a Q1 query over the VISIBLE working set. The result can never include a denied object
   *  because the working set itself excludes them (Glomar). Errors use the Glomar-safe taxonomy
   *  (denied-because-invisible == genuinely-unknown). */
  const runQuery = (query: TableQuery): ReturnType<typeof executeQuery> | { error: string; code: string } => {
    const fromName = typeof query.from === "string" ? query.from : query.from?.table;
    if (typeof fromName !== "string" || !workingSet.has(fromName)) {
      return { error: `unknown table "${String(fromName)}"`, code: "unknown_table" };
    }
    const rows = workingSet.getTable(fromName);
    if (!rows) return { error: `unknown table "${fromName}"`, code: "unknown_table" };
    try {
      return executeQuery(query, rows, workingSet.zonesPolygonMap());
    } catch (e) {
      if (e instanceof QueryError) return { error: e.message, code: e.code };
      return { error: "query failed", code: "internal_error" };
    }
  };

  // The rerouted request_evac command (host-gated, human-only; see copilotEvac.ts). Publish is
  // bound at activate() so the capability never holds the bus before the host hands it over.
  let evacCommand: CommandSpec | undefined;

  const capability: Capability = {
    id,

    activate(hostCtx: HostContext): void {
      ctx = hostCtx;

      // 1. Out-of-map extension: the dockable "COP Copilot" slideout (location: 'right').
      disposables.push(ctx.registerPanel({ id: `${id}:panel`, title: "COP Copilot", location: "right" }));

      // 2. The analyst verbs (ask / submit). Read-safe; any actor class may drive them.
      disposables.push(
        ctx.registerCommand({
          id: COPILOT_CMD_ASK,
          title: "Copilot: Ask",
          category: "Copilot",
          run: (args) => {
            const text = argText(args);
            if (text.trim()) void api.ask(text);
          },
        }),
      );
      disposables.push(
        ctx.registerCommand({
          id: COPILOT_CMD_SUBMIT,
          title: "Copilot: Submit",
          category: "Copilot",
          run: (args) => {
            const text = argText(args);
            if (text.trim()) void api.ask(text);
          },
        }),
      );

      // 3. The Q1 geo-query verb — runs over the VISIBLE working set only.
      disposables.push(
        ctx.registerCommand({
          id: COPILOT_CMD_QUERY,
          title: "Copilot: Run geo-query",
          category: "Copilot",
          run: (args) => {
            if (args && typeof args === "object" && "from" in args) runQuery(args as TableQuery);
          },
        }),
      );

      // 4. The REROUTED maps.* verbs — host-mediated, NO direct engine write.
      //    highlight / clear / filter record INTENT only in Wave A (no live host-paint wiring yet);
      //    the host applies them to its OWN highlight/filter layer in Wave B. The capability never
      //    calls engine.highlightRows / clearHighlights / filterLayer (those don't even exist on the
      //    write-free CapabilityEngine) — it contributes a command the host executes.
      disposables.push(
        ctx.registerCommand({
          id: COPILOT_CMD_HIGHLIGHT,
          title: "Copilot: Highlight rows",
          category: "Copilot",
          // Wave A: records intent; Wave B binds this to the host's sole-writer paint path.
          run: () => {},
        }),
      );
      disposables.push(
        ctx.registerCommand({
          id: COPILOT_CMD_CLEAR_HIGHLIGHTS,
          title: "Copilot: Clear highlights",
          category: "Copilot",
          run: () => {},
        }),
      );
      disposables.push(
        ctx.registerCommand({
          id: COPILOT_CMD_FILTER_LAYER,
          title: "Copilot: Filter layer",
          category: "Copilot",
          // The capability DECLARES the predicate; the host applies it to its own layer (Wave B).
          run: () => {},
        }),
      );

      // 5. set_viewport survives DIRECTLY: setView is on the write-free CapabilityEngine camera
      //    surface (render-engine.ts) — a read/camera op, not an entity/layer write. This is the
      //    one map verb that reroutes to ctx.engine.* and still passes no-bypass-check.ts.
      disposables.push(
        ctx.registerCommand({
          id: "scop:cmd:copilot-set-viewport",
          title: "Copilot: Set viewport",
          category: "Copilot",
          run: (args) => {
            const a = (args ?? {}) as { center?: [number, number]; zoom?: number };
            if (a.center && typeof a.center[0] === "number" && typeof a.center[1] === "number") {
              ctx!.engine.setView({ longitude: a.center[0], latitude: a.center[1], zoom: a.zoom ?? ctx!.engine.getView().zoom });
            }
          },
        }),
      );

      // 6. The host-gated request_evac (irreversible + human-only). Publish (host paints) is the
      //    only side effect, and the host human-gate blocks dispatch in Wave A (no confirm hook).
      evacCommand = createRequestEvacCommand({
        selfLocation,
        publish: (env) => ctx!.data.publish(env),
      });
      disposables.push(ctx.registerCommand(evacCommand));

      // 7. READ the envelope bus — the HOST-FILTERED (PDP-interposed) delivery ONLY. Every env
      //    here is already cleared-visible; a denied env is absent (Glomar). This is the sole
      //    feed into the working set — no private store, no static `visible:true`.
      disposables.push(
        ctx.data.subscribe({ kinds: COPILOT_KINDS }, (env) => {
          workingSet.ingest(env);
        }),
      );
    },

    deactivate(): void {
      for (const d of disposables.splice(0)) {
        try {
          d.dispose();
        } catch {
          // teardown is best-effort; the host's registry is the source of truth
        }
      }
      ctx = undefined;
      evacCommand = undefined;
    },
  };

  return { capability, api };
}
